import { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";

const customStyles = {
  menu: (base) => ({ ...base, zIndex: 9999 }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    maxHeight: "3.5vw",
    overflowY: "auto",
  }),
  control: (baseStyles) => ({ ...baseStyles, flexWrap: "nowrap" }),
};

const operatingCountries = ["MX", "CO", "PE", "US"];

const PACKAGING_TYPE_OPTIONS = [
  { value: "BOLSA", label: "Bolsa", abbreviation: "B" },
  { value: "CAJA", label: "Caja", abbreviation: "C" },
];

const EditPackagingModalComponent = ({
  show,
  handleClose,
  idPackaging = null,
  packaging = null,
  successfulUpdatePackaging = false,
  reduxGetPackagingById = () => {},
  reduxUpdatePackaging = () => {},
  countries = {},
  reduxGetCountries = () => {},
  logisticsOperators = {},
  reduxGetLogisticsOperators = () => {},
}) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    watch,
    setValue,
    reset,
  } = useForm();

  const watchCountryId = watch("countryId");
  const watchPackagingType = watch("packagingType");
  const watchCode = watch("code");

  const userData = useMemo(() => {
    try {
      return JSON.parse(window.localStorage.getItem('userData')) || {};
    } catch {
      return {};
    }
  }, []);

  const allowAllCountries = userData.allowAllCountries !== false;
  const forcedCountryId = !allowAllCountries ? userData.idCountry : null;

  const isDirty =
    dirtyFields?.countryId ||
    dirtyFields?.packagingType ||
    dirtyFields?.name ||
    dirtyFields?.length ||
    dirtyFields?.width ||
    dirtyFields?.height ||
    dirtyFields?.tolerancePercentage ||
    dirtyFields?.operatorId;

  const isAvailableToSubmit =
    isDirty &&
    watch("countryId") &&
    watch("packagingType") &&
    watch("name");

  const countriesItems = useMemo(
    () => {
      let items = Array.isArray(countries?.items) ? countries.items : [];
      if (!allowAllCountries && forcedCountryId) {
        items = items.filter((c) => c.idCountry === forcedCountryId);
      } else if (items.some((c) => c.isoCode)) {
        items = items.filter((c) => operatingCountries.includes(c.isoCode));
      }
      return items;
    },
    [countries.items, allowAllCountries, forcedCountryId],
  );

  const countryOptions = useMemo(
    () =>
      countriesItems.map((country) => ({
        value: country.idCountry,
        label: country.description,
        isoCode: country.isoCode,
      })),
    [countriesItems],
  );

  const measurementUnit = useMemo(() => {
    if (!watchCountryId) return "cm";
    const country = countryOptions.find((c) => c.value === watchCountryId);
    if (!country) return "cm";
    return country.isoCode === "US" ? "in" : "cm";
  }, [watchCountryId, countryOptions]);

  const codePrefix = useMemo(() => {
    if (!watchCountryId || !watchPackagingType) return "";
    const country = countryOptions.find((c) => c.value === watchCountryId);
    const typeOption = PACKAGING_TYPE_OPTIONS.find(
      (t) => t.value === watchPackagingType,
    );
    if (!country?.isoCode || !typeOption?.abbreviation) return "";
    return country.isoCode + typeOption.abbreviation;
  }, [watchCountryId, watchPackagingType, countryOptions]);

  useEffect(() => {
    if (show === false) return;
    reset();
    reduxGetCountries({
      page: 1,
      offset: 1000,
      search: "isActive|true",
    });
    reduxGetLogisticsOperators({ includeAll: true });
    if (idPackaging) {
      reduxGetPackagingById({ id: idPackaging });
    }
  }, [show, idPackaging, reduxGetCountries, reduxGetLogisticsOperators, reduxGetPackagingById, reset]);

  useEffect(() => {
    if (show === false || !packaging) return;

    reset({
      countryId: packaging.countryId,
      packagingType: packaging.packagingType,
      code: packaging.code,
      name: packaging.name,
      length: packaging.length,
      width: packaging.width,
      height: packaging.height,
      tolerancePercentage: packaging.tolerancePercentage,
      operatorId: packaging.operatorId,
    });
  }, [show, packaging, reset]);

  useEffect(() => {
    if (!codePrefix) return;

    const currentCode = watchCode || "";
    const currentPrefix = currentCode.slice(0, 3);

    if (currentPrefix !== codePrefix) {
      const suffix = currentCode.length > 3 ? currentCode.slice(3) : "";
      setValue("code", codePrefix + suffix, { shouldDirty: true });
    }
  }, [codePrefix, watchCode, setValue]);

  const prevSuccessfulUpdate = useRef(successfulUpdatePackaging);

  useEffect(() => {
    if (successfulUpdatePackaging && !prevSuccessfulUpdate.current) {
      reset();
      handleClose();
    }
    prevSuccessfulUpdate.current = successfulUpdatePackaging;
  }, [successfulUpdatePackaging, reset, handleClose]);

  const onSubmit = (data) => {
    const payload = {
      id: idPackaging,
      countryId: data.countryId,
      packagingType: data.packagingType,
      code: data.code,
      name: data.name.trim(),
    };
    if (data.length !== undefined) payload.length = data.length;
    if (data.width !== undefined) payload.width = data.width;
    if (data.height !== undefined) payload.height = data.height;
    if (data.tolerancePercentage !== undefined)
      payload.tolerancePercentage = data.tolerancePercentage;
    if (data.operatorId !== undefined) payload.operatorId = data.operatorId;

    reduxUpdatePackaging(payload);
  };

  return (
      <Modal
        size="lg"
        show={show}
        onHide={handleClose}
        onEscapeKeyDown={handleClose}
        backdrop="static"
        keyboard={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Editar Embalaje {packaging?.code || ""}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Row>
              <Form.Group as={Col} className="px-3 pb-2">
                <label htmlFor="countryId" className="form-label">País*</label>
                <Controller
                  name="countryId"
                  control={control}
                  rules={{ required: "Es obligatorio seleccionar un país" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      className={`${!!errors?.countryId && "is-invalid"}`}
                      styles={customStyles}
                      options={countryOptions}
                      isDisabled={!allowAllCountries}
                      value={
                        field.value
                          ? countryOptions.find(
                              (option) => option.value === field.value,
                            )
                          : null
                      }
                      onChange={(selectedOption) => {
                        field.onChange(
                          selectedOption ? selectedOption.value : null,
                        );
                        setValue("operatorId", null);
                      }}
                    />
                  )}
                />
                {!!errors?.countryId && (
                  <span className="error-message">
                    {errors?.countryId?.message || "Este campo es obligatorio"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} className="px-3 pb-2">
                <label htmlFor="packagingType" className="form-label">
                  Tipo de Embalaje*
                </label>
                <Controller
                  name="packagingType"
                  control={control}
                  rules={{
                    required: "Es obligatorio seleccionar un tipo de embalaje",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      className={`${!!errors?.packagingType && "is-invalid"}`}
                      styles={customStyles}
                      options={PACKAGING_TYPE_OPTIONS}
                      value={
                        field.value
                          ? PACKAGING_TYPE_OPTIONS.find(
                              (option) => option.value === field.value,
                            )
                          : null
                      }
                      onChange={(selectedOption) =>
                        field.onChange(
                          selectedOption ? selectedOption.value : null,
                        )
                      }
                    />
                  )}
                />
                {!!errors?.packagingType && (
                  <span className="error-message">
                    {errors?.packagingType?.message ||
                      "Este campo es obligatorio"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} className="px-3 pb-2">
                <label htmlFor="code" className="form-label">Código*</label>
                <input
                  {...register("code", {
                    required: "El código es obligatorio",
                    pattern: {
                      value: /^[A-Z]{2}[BC]\d{3}$/,
                      message:
                        "Formato inválido — debe ser 2 letras + B/C + 3 dígitos (ej. COB001)",
                    },
                  })}
                  id="code"
                  type="text"
                  disabled
                  className={`form-control ${!!errors?.code && "is-invalid"}`}
                />
                {!!errors?.code && (
                  <span className="error-message">
                    {errors?.code?.message || "Este campo es obligatorio"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} className="px-3 pb-2">
                <label htmlFor="name" className="form-label">Nombre*</label>
                <input
                  {...register("name", {
                    required: "El nombre es obligatorio",
                    maxLength: {
                      value: 100,
                      message: "Máximo 100 caracteres en este campo",
                    },
                  })}
                  id="name"
                  type="text"
                  className={`form-control ${!!errors?.name && "is-invalid"}`}
                />
                {!!errors?.name && (
                  <span className="error-message">
                    {errors?.name?.message || "Este campo es obligatorio"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} md={4} className="px-3 pb-2">
                <label htmlFor="length" className="form-label">Largo ({measurementUnit})</label>
                <input
                  {...register("length", {
                    min: { value: 0.01, message: "El valor mínimo es 0.01" },
                    valueAsNumber: true,
                  })}
                  id="length"
                  type="number"
                  step="0.01"
                  className={`form-control ${!!errors?.length && "is-invalid"}`}
                />
                {!!errors?.length && (
                  <span className="error-message">
                    {errors?.length?.message || "Valor inválido"}
                  </span>
                )}
              </Form.Group>
              <Form.Group as={Col} md={4} className="px-3 pb-2">
                <label htmlFor="width" className="form-label">Ancho ({measurementUnit})</label>
                <input
                  {...register("width", {
                    min: { value: 0.01, message: "El valor mínimo es 0.01" },
                    valueAsNumber: true,
                  })}
                  id="width"
                  type="number"
                  step="0.01"
                  className={`form-control ${!!errors?.width && "is-invalid"}`}
                />
                {!!errors?.width && (
                  <span className="error-message">
                    {errors?.width?.message || "Valor inválido"}
                  </span>
                )}
              </Form.Group>
              <Form.Group as={Col} md={4} className="px-3 pb-2">
                <label htmlFor="height" className="form-label">Alto ({measurementUnit})</label>
                <input
                  {...register("height", {
                    min: { value: 0.01, message: "El valor mínimo es 0.01" },
                    valueAsNumber: true,
                  })}
                  id="height"
                  type="number"
                  step="0.01"
                  className={`form-control ${!!errors?.height && "is-invalid"}`}
                />
                {!!errors?.height && (
                  <span className="error-message">
                    {errors?.height?.message || "Valor inválido"}
                  </span>
                )}
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} md={6} className="px-3 pb-2">
                <label htmlFor="tolerancePercentage" className="form-label">
                  Tolerancia % (máx 50)
                </label>
                <input
                  {...register("tolerancePercentage", {
                    min: { value: 0, message: "El valor mínimo es 0" },
                    max: { value: 50, message: "El valor máximo es 50%" },
                    valueAsNumber: true,
                  })}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val > 50) setValue("tolerancePercentage", 50, { shouldDirty: true });
                    if (val < 0) setValue("tolerancePercentage", 0, { shouldDirty: true });
                  }}
                  id="tolerancePercentage"
                  type="number"
                  step="0.01"
                  className={`form-control ${!!errors?.tolerancePercentage && "is-invalid"}`}
                />
                {!!errors?.tolerancePercentage && (
                  <span className="error-message">
                    {errors?.tolerancePercentage?.message || "Valor inválido"}
                  </span>
                )}
              </Form.Group>
              <Form.Group as={Col} md={6} className="px-3 pb-2">
                <label htmlFor="operatorId" className="form-label">
                  Operador
                </label>
                <Controller
                  name="operatorId"
                  control={control}
                  render={({ field }) => {
                    const items = Array.isArray(logisticsOperators?.items)
                      ? logisticsOperators.items
                      : [];
                    const options = items
                      .filter((f) => f.isActive === true)
                      .filter((f) => !watchCountryId || f.country === watchCountryId)
                      .map((ele) => ({
                        value: ele.idCompany,
                        label: ele.description,
                      }));
                    return (
                      <Select
                        {...field}
                        isClearable
                        styles={customStyles}
                        options={options}
                        value={
                          field.value
                            ? options.find((o) => o.value === field.value)
                            : null
                        }
                      onChange={(selectedOption) =>
                        field.onChange(
                          selectedOption ? selectedOption.value : null,
                        )
                      }
                      />
                    );
                  }}
                />
              </Form.Group>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!isAvailableToSubmit}
            >
              Guardar Cambios
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
  );
};

const mapStateToProps = (state) => ({
  packaging: state.packagingState.rowEdited,
  successfulUpdatePackaging: state.packagingState.successfulUpdatePackaging,
  countries: state.countryState.countries,
  logisticsOperators: state.companyState.logisticsOperators,
});

const mapDispatchToProps = (dispatch) => ({
  reduxGetPackagingById: (payload) =>
    dispatch({ type: "READ_PACKAGING_REQUEST", value: payload }),
  reduxUpdatePackaging: (payload) =>
    dispatch({ type: "UPDATE_PACKAGING_REQUEST", value: payload }),
  reduxGetCountries: (payload) =>
    dispatch({ type: "FETCH_COUNTRIES_REQUEST", value: payload }),
  reduxGetLogisticsOperators: (payload) =>
    dispatch({
      type: "FETCH_COMPANIESBYLOGISTICSOPERATOR_REQUEST",
      value: payload,
    }),
});

export const EditPackagingModal = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditPackagingModalComponent);
